import { useState, useCallback, useEffect, useMemo } from 'react'
import { User } from 'firebase/auth'
import { toast } from 'sonner'
import { LockedSalaryDataService } from '@/services/lockedSalaryDataService'
import { LockedSalaryData } from '@/types/lockedSalaryData'
import { getAmountToPay } from '@/services/lockedDataAdapter'
import { sumMoney } from '@/lib/money'
import { functions, httpsCallable } from '@/lib/firebase'
import { generateAndDownloadPdf, generateBulkPdfsAsZip } from '@/services/exportPdfService'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'
import { FeatureFlag } from '@/constants/featureFlags'

/**
 * Email statistics for the export page
 */
export interface EmailStats {
  withEmail: number      // Drivers with email address
  withoutEmail: number   // Drivers missing email
  alreadySent: number    // Drivers who received email
  notYetSent: number     // Drivers pending (withEmail - alreadySent)
}

/**
 * Totals for summary display
 */
export interface ExportTotals {
  totalGrossSalary: number
  totalPayout: number
  driverCount: number
}

/**
 * Bulk operation progress state
 */
interface BulkProgress {
  isOpen: boolean
  isRunning: boolean
  current: number
  total: number
  errors: string[]
}

/**
 * Hook return type
 */
export interface ExportController {
  // Data
  lockedData: LockedSalaryData[]
  isLoading: boolean
  error: string | null

  // Computed
  totals: ExportTotals
  emailStats: EmailStats

  // Individual operations
  sendingEmailTo: string | null
  downloadingPdfFor: string | null

  // Bulk operations
  bulkEmailProgress: BulkProgress
  bulkPdfProgress: BulkProgress
  showBulkEmailConfirm: boolean

  // Actions
  loadData: () => Promise<void>
  sendEmailToDriver: (lockedData: LockedSalaryData) => Promise<void>
  downloadPdfForDriver: (lockedData: LockedSalaryData) => Promise<void>
  openBulkEmailConfirm: () => void
  closeBulkEmailConfirm: () => void
  sendBulkEmails: () => Promise<void>
  downloadAllPdfs: () => Promise<void>
  closeBulkEmailProgress: () => void
  closeBulkPdfProgress: () => void
}

const EMAIL_DELAY_MS = 1000

const INITIAL_BULK_PROGRESS: BulkProgress = {
  isOpen: false,
  isRunning: false,
  current: 0,
  total: 0,
  errors: []
}

/**
 * Controller hook for the export page
 * Manages locked salary data, email sending, and PDF generation
 */
export function useExportController(
  user: User | null,
  period: string | undefined
): ExportController {
  const multiCompanyEnabled = useFeatureFlag(FeatureFlag.MULTI_COMPANY)

  // Data state
  const [lockedData, setLockedData] = useState<LockedSalaryData[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Individual operation state
  const [sendingEmailTo, setSendingEmailTo] = useState<string | null>(null)
  const [downloadingPdfFor, setDownloadingPdfFor] = useState<string | null>(null)

  // Bulk operation state
  const [bulkEmailProgress, setBulkEmailProgress] = useState<BulkProgress>(INITIAL_BULK_PROGRESS)
  const [bulkPdfProgress, setBulkPdfProgress] = useState<BulkProgress>(INITIAL_BULK_PROGRESS)
  const [showBulkEmailConfirm, setShowBulkEmailConfirm] = useState(false)

  // Compute totals from locked data
  const totals = useMemo<ExportTotals>(() => ({
    totalGrossSalary: sumMoney(lockedData.map(data => data.totalGrossSalary || 0)),
    totalPayout: sumMoney(lockedData.map(data => data.aggregatedTotals ? getAmountToPay(data.aggregatedTotals) : 0)),
    driverCount: lockedData.length
  }), [lockedData])

  // Compute email statistics
  const emailStats = useMemo<EmailStats>(() => {
    let withEmail = 0
    let withoutEmail = 0
    let alreadySent = 0

    for (const data of lockedData) {
      if (data.driverInfo.email) {
        withEmail++
        if (data.emailSentAt) alreadySent++
      } else {
        withoutEmail++
      }
    }

    return { withEmail, withoutEmail, alreadySent, notYetSent: withEmail - alreadySent }
  }, [lockedData])

  // Load locked data for the period
  const loadData = useCallback(async () => {
    if (!user || !period) return

    setIsLoading(true)
    setError(null)

    try {
      const data = await LockedSalaryDataService.getLockedDataForPeriod(user, period)
      setLockedData(data)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Kunde inte hämta låst data'
      setError(message)
      toast.error(message)
    } finally {
      setIsLoading(false)
    }
  }, [user, period])

  // Load data on mount and when period changes
  useEffect(() => {
    loadData()
  }, [loadData])

  // Send email to a single driver
  const sendEmailToDriver = useCallback(async (data: LockedSalaryData) => {
    if (!user || !period) return
    if (!data.driverInfo.email) {
      toast.error('Föraren har ingen e-postadress')
      return
    }

    setSendingEmailTo(data.driverId)

    try {
      const sendEmail = httpsCallable<
        { driverId: string; periodName: string },
        { success: boolean; messageId?: string }
      >(functions, 'sendSalaryEmail')

      await sendEmail({ driverId: data.driverId, periodName: period })
      await LockedSalaryDataService.updateEmailSent(user, data.id)
      await loadData()

      toast.success(`Email skickat till ${data.driverInfo.email}`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Kunde inte skicka email')
    } finally {
      setSendingEmailTo(null)
    }
  }, [user, period, loadData])

  // Download PDF for a single driver
  const downloadPdfForDriver = useCallback(async (data: LockedSalaryData) => {
    if (!user || !period) return

    setDownloadingPdfFor(data.driverId)

    try {
      await generateAndDownloadPdf(user, data, period, multiCompanyEnabled)
      toast.success('PDF nedladdad')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Kunde inte generera PDF')
    } finally {
      setDownloadingPdfFor(null)
    }
  }, [user, period])

  // Dialog controls
  const openBulkEmailConfirm = useCallback(() => setShowBulkEmailConfirm(true), [])
  const closeBulkEmailConfirm = useCallback(() => setShowBulkEmailConfirm(false), [])
  const closeBulkEmailProgress = useCallback(() => {
    setBulkEmailProgress(prev => ({ ...prev, isOpen: false }))
  }, [])
  const closeBulkPdfProgress = useCallback(() => {
    setBulkPdfProgress(prev => ({ ...prev, isOpen: false }))
  }, [])

  // Send bulk emails to all drivers who haven't received one yet
  const sendBulkEmails = useCallback(async () => {
    if (!user || !period) return

    const driversToEmail = lockedData.filter(d => d.driverInfo.email && !d.emailSentAt)

    if (driversToEmail.length === 0) {
      toast.info('Alla förare med email har redan fått sina lönespecifikationer')
      setShowBulkEmailConfirm(false)
      return
    }

    setShowBulkEmailConfirm(false)
    setBulkEmailProgress({
      isOpen: true,
      isRunning: true,
      current: 0,
      total: driversToEmail.length,
      errors: []
    })

    const sendEmail = httpsCallable<
      { driverId: string; periodName: string },
      { success: boolean; messageId?: string }
    >(functions, 'sendSalaryEmail')

    const errors: string[] = []

    for (let i = 0; i < driversToEmail.length; i++) {
      const data = driversToEmail[i]

      try {
        await sendEmail({ driverId: data.driverId, periodName: period })
        await LockedSalaryDataService.updateEmailSent(user, data.id)
      } catch (err) {
        errors.push(`${data.driverInfo.name}: ${err instanceof Error ? err.message : 'Okänt fel'}`)
      }

      setBulkEmailProgress(prev => ({ ...prev, current: i + 1, errors }))

      if (i < driversToEmail.length - 1) {
        await new Promise(resolve => setTimeout(resolve, EMAIL_DELAY_MS))
      }
    }

    setBulkEmailProgress(prev => ({ ...prev, isRunning: false }))
    await loadData()

    const sentCount = driversToEmail.length - errors.length
    if (errors.length === 0) {
      toast.success(`${sentCount} email skickade`)
    } else {
      toast.warning(`${sentCount} email skickade, ${errors.length} misslyckades`)
    }
  }, [user, period, lockedData, loadData])

  // Download all PDFs as ZIP
  const downloadAllPdfs = useCallback(async () => {
    if (!user || !period || lockedData.length === 0) return

    setBulkPdfProgress({
      isOpen: true,
      isRunning: true,
      current: 0,
      total: lockedData.length,
      errors: []
    })

    const result = await generateBulkPdfsAsZip(
      user,
      lockedData,
      period,
      (current, total, errors) => {
        setBulkPdfProgress(prev => ({ ...prev, current, errors }))
      },
      multiCompanyEnabled
    )

    setBulkPdfProgress(prev => ({ ...prev, isRunning: false }))

    if (result.successCount > 0) {
      if (result.errors.length === 0) {
        toast.success(`ZIP-fil med ${result.successCount} lönespecifikationer nedladdad`)
      } else {
        toast.warning(`${result.successCount} PDF:er nedladdade, ${result.errors.length} misslyckades`)
      }
    } else {
      toast.error('Kunde inte generera några PDF:er')
    }
  }, [user, period, lockedData])

  return {
    lockedData,
    isLoading,
    error,
    totals,
    emailStats,
    sendingEmailTo,
    downloadingPdfFor,
    bulkEmailProgress,
    bulkPdfProgress,
    showBulkEmailConfirm,
    loadData,
    sendEmailToDriver,
    downloadPdfForDriver,
    openBulkEmailConfirm,
    closeBulkEmailConfirm,
    sendBulkEmails,
    downloadAllPdfs,
    closeBulkEmailProgress,
    closeBulkPdfProgress
  }
}
